import { Injectable } from '@angular/core'
import { BehaviorSubject, filter, firstValueFrom, from, of, switchMap, take } from 'rxjs'
import { Doka360ProjectsRepository } from '../repositories/doka360-projects.repository'
import { Router, NavigationEnd } from '@angular/router'
import { Doka360ProjectModel } from '../../generated/efp-api'

@Injectable({
  providedIn: 'root',
})
export class Doka360ProjectSelectorService {
  private currentDoka360ProjectUid = new BehaviorSubject<string | undefined>(undefined)

  public currentDoka360ProjectUid$ = this.currentDoka360ProjectUid.asObservable()

  public doka360Project$ = this.currentDoka360ProjectUid$.pipe(
    switchMap((uid) => {
      if (uid) {
        return from(this.doka360ProjectsRepository.findOneById(uid))
      } else {
        return of(undefined)
      }
    })
  )

  constructor(
    private readonly doka360ProjectsRepository: Doka360ProjectsRepository,
    private readonly router: Router
  ) {
    // Step 1: Apply project ID from initial URL
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        const url = new URL(window.location.href)
        const projectUid = url.searchParams.get('doka360ProjectUid')
        if (projectUid) {
          this.setCurrentDoka360ProjectUid(projectUid)
        }
      })

    // Step 2: Re-append the UID to future navigations
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        const currentUid = this.getCurrentDoka360ProjectUid()
        const url = new URL(window.location.href)
        const queryParams = new URLSearchParams(url.search)

        // If the UID exists in the service but not in the URL → add it
        if (currentUid && queryParams.get('doka360ProjectUid') !== currentUid) {
          queryParams.set('doka360ProjectUid', currentUid)

          // Replace URL without reloading
          const newUrl = `${url.pathname}?${queryParams.toString()}${url.hash}`
          window.history.replaceState({}, '', newUrl)
        }
      })
  }

  public setCurrentDoka360ProjectUid(uid: string | undefined): void {
    if (this.getCurrentDoka360ProjectUid() !== uid) {
      this.currentDoka360ProjectUid.next(uid)
    }
  }

  public getCurrentDoka360ProjectUid(): string | undefined {
    return this.currentDoka360ProjectUid.getValue()
  }

  public async getCurrentDoka360Project(): Promise<Doka360ProjectModel | undefined> {
    return firstValueFrom(this.doka360Project$)
  }
}
